// The workbench's SAVED H3 layers.
//
// The lab (`/_/map-lab`) compares encodings of the district's fixed datasets and
// keeps its selection in reactive state; the workbench instead lets the operator
// SAVE a layer — named, org-scoped, read back from the database — as 1..3
// VARIABLES, each a series catalogue source plus a metric, and toggle it beside
// the standing layers.
//
// This module is the pure half of that: what a definition may be, the two tile
// URLs its map is built from, and the encoding the map is painted from. It is
// pure (no Vue, no store, no map) for the same reason the rest of `ts/lib/h3` is,
// and it READS the shared catalogues rather than restating them — `SERIES_ROLES`
// for the slot colours, `SERIES_CATALOGUE` for the sources, `slotSeries` for the
// drawn series, `seriesTiles`/`outlineClusterTiles` for the URLs — so a saved
// layer cannot mean one thing in the tray and another on the map.
//
// Composition follows the variable COUNT, exactly as the lab's third variable
// taught it: one variable is the single sequential ramp (the fill painted with a
// spotlight on that one series), two make the fill the 3×3 bivariate, and three
// add the dissolved outline on the third variable. The one thing the workbench
// adds is that a variable's colour is the OPERATOR's: the lab's roles each own a
// fixed ramp, so the single-variable ramp is mixed from the variable's own
// colour here (`rampFromColor`) while the bivariate keeps the lab's constant
// palette, whose two axes must stay the same blue and orange whoever the
// variables are.
import type {
	MapReviewH3LayerDefinition,
	MapReviewH3Metric,
	MapReviewH3Variable,
} from "@/type/api";
import {
	type MultiDomains,
	type MultiEncode,
	type MultiSeries,
	slotSeries,
} from "@/lib/h3/multi";
import { type OutlineClass, outlineClusterTiles } from "@/lib/h3/outline";
import {
	SERIES_CATALOGUE,
	SERIES_ROLES,
	type SeriesCatalogueEntry,
	seriesMeasureByID,
	seriesMeasureUnit,
	seriesTiles,
} from "@/lib/h3/series";

/** How many variables a saved layer may hold. It is the number of roles the lab
 * draws one variable each for — a sequential fill, the bivariate's second axis,
 * and the outline — so a fourth would be a fourth colour in a cell that already
 * carries three encodings. */
export const H3_MAX_VARIABLES = 3;

/** The colour a variable takes when the operator has not chosen one: the colour
 * of the ROLE at its position — Fill A's blue, Fill B's orange, Outline C's
 * green — so a layer saved with the defaults is drawn in the lab's own
 * colourblind-safe set rather than in a palette invented here. */
export const H3_VARIABLE_COLORS: string[] = SERIES_ROLES.slice(
	0,
	H3_MAX_VARIABLES,
).map((role) => role.color);

/** The definition a new layer opens with: one variable — the treatments table,
 * counted — which draws the plain sequential ramp, the smallest thing that is
 * worth saving. */
export function defaultH3Definition(): MapReviewH3LayerDefinition {
	return {
		variables: [
			{
				source: 0,
				metric: "records",
				unit: "",
				color: H3_VARIABLE_COLORS[0],
			},
		],
	};
}

/** The definition's variables, tolerating the wire: a definition read back from
 * the API is typed but not trusted, and a malformed one must draw nothing rather
 * than throw while the map is being built. */
function variablesOf(
	def: MapReviewH3LayerDefinition | null | undefined,
): MapReviewH3Variable[] {
	return def && Array.isArray(def.variables) ? def.variables : [];
}

/** The catalogue entry a variable counts, or null when its `source` names none.
 * The one place a stored index is resolved against the catalogue, so a
 * definition's validity and the label it draws cannot disagree about which
 * predicate it is. */
export function catalogueEntry(
	variable: MapReviewH3Variable,
): SeriesCatalogueEntry | null {
	return (
		SERIES_CATALOGUE.find((entry) => entry.index === variable.source) ?? null
	);
}

function isMetric(value: unknown): value is MapReviewH3Metric {
	return value === "records" || value === "area" || value === "quantity";
}

/**
 * Why a definition cannot be saved, or null when it can.
 *
 * The rules are mirrored on the server (`platform/map_review/h3_layer.go`), so the
 * editor can refuse an edit without a round trip and both agree on what a
 * definition is:
 *
 *   * one to three variables, in draw order;
 *   * a `source` that is one of the series catalogue's own indexes (a
 *     high-cardinality column is a ranking, not a fill, so it is not in the
 *     catalogue at all);
 *   * a catalogue entry that carries no area or quantity may only be COUNTED —
 *     its rows have no area_treated/quantity to sum, and a silent zero would be
 *     a blank map the operator could not explain;
 *   * a `unit` exactly when the metric is `quantity` (a sum within one unit
 *     means nothing without the unit), and an empty one otherwise, since an
 *     acreage is read in the district's own unit and a count has none;
 *   * a `#rrggbb` colour.
 *
 * The message is the sentence the form shows, and it names the variable the way
 * the operator sees it ("variable 2"), not by array index.
 */
export function validateH3Definition(
	def: MapReviewH3LayerDefinition,
): string | null {
	const variables: unknown = (def as { variables?: unknown } | null | undefined)
		?.variables;
	if (!Array.isArray(variables) || variables.length === 0) {
		return "a layer needs at least one variable";
	}
	if (variables.length > H3_MAX_VARIABLES) {
		return `a layer can hold at most ${H3_MAX_VARIABLES} variables`;
	}
	for (let index = 0; index < variables.length; index++) {
		const problem = validateVariable(variables[index], index);
		if (problem) return problem;
	}
	return null;
}

function validateVariable(value: unknown, index: number): string | null {
	const named = `variable ${index + 1}`;
	if (typeof value !== "object" || value === null) {
		return `${named} is not a variable`;
	}
	const variable = value as Partial<MapReviewH3Variable>;
	const source = variable.source;
	if (typeof source !== "number" || !Number.isInteger(source)) {
		return `${named} names no series source`;
	}
	const entry = SERIES_CATALOGUE.find((item) => item.index === source) ?? null;
	if (!entry) return `${named} names no series source`;
	if (!isMetric(variable.metric)) {
		return `${named} has no metric`;
	}
	if (variable.metric !== "records" && !entry.measurable) {
		return `${named}: “${entry.label}” carries no area or quantity, so it can only be counted`;
	}
	const unit = variable.unit;
	if (typeof unit !== "string") {
		return `${named} has no unit`;
	}
	if (variable.metric === "quantity" && unit === "") {
		return `${named}: a quantity needs the unit it is summed within`;
	}
	if (variable.metric !== "quantity" && unit !== "") {
		return `${named}: only a quantity carries a unit`;
	}
	const color = variable.color;
	if (typeof color !== "string" || !/^#[0-9a-fA-F]{6}$/.test(color)) {
		return `${named} needs a #rrggbb colour`;
	}
	return null;
}

/** The metric's ordinal as the tiles read it (`h3_series_m{slot}` and
 * `h3_outline_multi_m{slot}` are these indexes): the lab's own `SERIES_MEASURES`
 * ordinals — 0 count, 1 SUM(area_treated), 2 SUM(quantity) — so the URL and the
 * tile server's CASE arms cannot drift. */
export function h3MetricIndex(metric: MapReviewH3Metric): number {
	return seriesMeasureByID(metric).index;
}

/** The word a variable's readout carries: the count's own word ("records"), the
 * district's area unit for an acreage, and the unit the quantity was recorded
 * in. It is the lab's rule, reused rather than restated. */
export function h3MetricUnitLabel(
	variable: MapReviewH3Variable,
	areaUnit: string,
): string {
	return seriesMeasureUnit(variable.metric, variable.unit, areaUnit);
}

/** The name a variable's readout carries: the catalogue entry it counts and the
 * metric it counts it by — "Treatments — chemical · Quantity applied" — so a
 * tray row and a key entry name both halves of the variable. The UNIT is
 * deliberately not part of it: the same label is read where the unit is already
 * stated, and `h3MetricUnitLabel` composes it where it is not. */
export function h3VariableLabel(variable: MapReviewH3Variable): string {
	const entry = catalogueEntry(variable);
	const measure = seriesMeasureByID(variable.metric).label;
	return entry ? `${entry.label} · ${measure}` : measure;
}

// ── The ramp ───────────────────────────────────────────────────────────────

type RGB = [number, number, number];

// The two ends the ramp is mixed toward, as channels. The dark one is the
// near-black the lab's own ramps end on (#0b0f14) rather than #000: a ramp that
// reached pure black would collapse its last class into ink that carries no hue
// at all.
const RAMP_LIGHT: RGB = [255, 255, 255];
const RAMP_DARK: RGB = [11, 15, 20];

// The identity colour a colour that is not `#rrggbb` ramps from: the first
// variable's default (#0072B2), so an invalid colour draws what
// `defaultH3Definition` would have written rather than nothing.
const RAMP_FALLBACK: RGB = parseHex(H3_VARIABLE_COLORS[0]) ?? [0, 114, 178];

// Above this relative luminance the identity colour is too pale to ramp UP from:
// lightening it would pile its first stops on white.
const RAMP_PALE = 0.72;

// The five stops, as fractions along the ramp: positive lightens toward white,
// negative darkens toward the near-black, and 0 is the colour itself. A pale
// identity darkens FROM itself — it is already at the ramp's light end — while a
// darker one lightens first and only then passes through its own shade. Named
// here because both tables are what make the ramp five distinguishable classes.
const RAMP_STOPS_DARKER = [0.86, 0.55, 0.28, 0, -0.55];
const RAMP_STOPS_PALER = [0, -0.2, -0.4, -0.6, -0.8];

function parseHex(color: string): RGB | null {
	const match = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(
		color,
	);
	if (!match) return null;
	return [
		Number.parseInt(match[1], 16),
		Number.parseInt(match[2], 16),
		Number.parseInt(match[3], 16),
	];
}

/** `base` sampled `t` of the way along the ramp, as hex: toward white when `t`
 * is positive, toward the near-black when it is negative, and the colour itself
 * at 0. The one arithmetic the ramp is built from — direction and formatting in
 * one place, so a stop cannot be mixed one way and printed another. */
function rampStop(base: RGB, t: number): string {
	const target = t >= 0 ? RAMP_LIGHT : RAMP_DARK;
	const amount = Math.abs(t);
	return `#${base
		.map((value, index) =>
			Math.round(value + (target[index] - value) * amount)
				.toString(16)
				.padStart(2, "0"),
		)
		.join("")}`;
}

/** A colour's perceived luminance (Rec. 601), 0..1 — how light it reads, which
 * is what decides the ramp's direction. */
function luminance([red, green, blue]: RGB): number {
	return (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
}

/**
 * The 5-stop sequential ramp a ONE-variable layer draws, derived from that
 * variable's colour: three steps in from white to the colour itself, then a
 * darker shade of it. It is the workbench's own ramp, because a saved variable's
 * colour is the operator's while the lab's roles own fixed ramps — and the 2-
 * and 3-variable compositions keep the lab's constant grid, whose axes are the
 * same blue and orange whoever the variables are.
 *
 * The ramp must run light → dark, so the identity colour picks the direction: a
 * pale one darkens (lightening it would collapse the top of the ramp into white)
 * and a dark one lightens. Five stops, all distinct, so the ramp's classes can be
 * told apart in the key.
 */
export function rampFromColor(color: string): string[] {
	const base = parseHex(color) ?? RAMP_FALLBACK;
	const stops =
		luminance(base) > RAMP_PALE ? RAMP_STOPS_PALER : RAMP_STOPS_DARKER;
	return stops.map((t) => rampStop(base, t));
}

// ── The tiles ──────────────────────────────────────────────────────────────

/** What a saved layer needs from the map to build its URLs: the tile base, the
 * live resolution, and the district's resolved area unit (which is not the
 * layer's to store — an acreage is read in whatever the district reads acreage
 * in). */
export interface H3TileOptions {
	base: string;
	resolution: number;
	areaUnit: string;
}

/**
 * The series grid tile for one saved layer: `h3-series` with each
 * variable in its own slot — its catalogue source, its metric and (for a
 * quantity) its unit — and the district's area unit for any acreage. Up to three
 * slots are set, which is the tile's whole series vocabulary.
 *
 * The layer has no window of its own, so both bounds are left unstated: a saved
 * layer describes the district's data, exactly as the standing layers beside it
 * do, and a window would be a second, invisible filter the tray never showed.
 */
export function h3SeriesGridTiles(
	def: MapReviewH3LayerDefinition,
	opts: H3TileOptions,
): string[] {
	const variables = variablesOf(def).slice(0, H3_MAX_VARIABLES);
	return seriesTiles({
		base: opts.base,
		resolution: opts.resolution,
		from: "",
		to: "",
		slots: variables.map((variable) => variable.source),
		measure: "records",
		quantityUnit: "",
		areaUnit: opts.areaUnit,
		// Index = slot: each variable's own metric, and the unit only a quantity
		// has one of (a null entry leaves the slot on the globals above).
		measures: variables.map((variable) => variable.metric),
		units: variables.map((variable) =>
			variable.metric === "quantity" ? variable.unit : null,
		),
	});
}

/** The class a layer's THIRD variable is drawn as: the multi dissolve reads its
 * slots' fields as `v_s2` (the dissolve's own index 6), `> 0` — the cells where
 * the variable actually is, which is the sparse exception an outline exists to
 * show and the only way a sparse third variable is visible at all. Null when
 * there is no third variable: an outline over one of the fills would be a fourth
 * colour in a cell that already carries three encodings. */
export function h3OutlineClass(
	def: MapReviewH3LayerDefinition,
): OutlineClass | null {
	const third = variablesOf(def)[2];
	if (!third) return null;
	return {
		id: "h3-outline",
		source: "multi",
		field: "v_s2",
		op: ">",
		threshold: 0,
		color: third.color || SERIES_ROLES[2].color,
		width: 2.5,
	};
}

/**
 * The dissolve tile for a layer's third variable — the outline drawn over the
 * bivariate fill — or null when the layer holds fewer than three variables.
 *
 * Its series context mirrors the fill's own slots, so the outline rings the
 * cells of the third variable under THE THIRD VARIABLE'S OWN METRIC: the
 * dissolve re-reads the base tables rather than joining the grid's tile, so a
 * class drawn in a different measure than the fill would be two questions in one
 * picture.
 */
export function h3OutlineTiles(
	def: MapReviewH3LayerDefinition,
	opts: H3TileOptions,
): string[] | null {
	const cls = h3OutlineClass(def);
	if (!cls) return null;
	const variables = variablesOf(def).slice(0, H3_MAX_VARIABLES);
	return outlineClusterTiles({
		cls,
		base: opts.base,
		resolution: opts.resolution,
		from: "",
		to: "",
		series: {
			measure: "records",
			quantityUnit: "",
			areaUnit: opts.areaUnit,
			slots: variables.map((variable) => variable.source),
			measures: variables.map((variable) => variable.metric),
			units: variables.map((variable) =>
				variable.metric === "quantity" ? variable.unit : null,
			),
		},
	});
}

// ── The encoding ───────────────────────────────────────────────────────────

/**
 * One variable as the drawn series the encodings read: the role at its position
 * (so variable 0 → `s0` …), the variable's own colour, and the catalogue entry's
 * label with its metric's. The `field` is `slotSeries`'s — `n_s{slot}` for a
 * count, `m_s{slot}` for a sum — i.e. the column the series tile really carries.
 *
 * The ramp is the variable's OWN, mixed from its colour, rather than the role's
 * fixed one: the colour is what the operator chose, so the scale it draws should
 * be a run of that colour.
 */
export function h3VariableSeries(
	def: MapReviewH3LayerDefinition,
	index: number,
): MultiSeries {
	const role = SERIES_ROLES[index] ?? SERIES_ROLES[0];
	const variable = variablesOf(def)[index];
	const color = variable?.color || role.color;
	const series = slotSeries(
		{
			role: role.id,
			index: variable?.source ?? role.slot,
			label: variable ? h3VariableLabel(variable) : role.label,
			color,
		},
		variable?.metric ?? "records",
	);
	return { ...series, ramp: rampFromColor(color) };
}

/**
 * The encoding a saved layer's map is painted from: every variable as a drawn
 * series, the domains the server counted for them, and — the lab's
 * single-variable look — a spotlight on `s0` when it is alone, which is the
 * branch of `bivariateFillColor` that paints one sequential ramp. Two or three
 * variables are not spotlighted, so the fill is the lab's 3×3 bivariate of the
 * first two.
 *
 * `maxCellTotal` is 0: it is the breakdown ramp's domain, and a saved layer
 * never draws the breakdown.
 */
export function h3Encode(
	def: MapReviewH3LayerDefinition,
	domains: MultiDomains,
): MultiEncode {
	const variables = variablesOf(def);
	const series = variables.map((_, index) => h3VariableSeries(def, index));
	return {
		series,
		domains,
		spotlight: series.length === 1 ? "s0" : null,
		maxCellTotal: 0,
	};
}
